import { useEffect, useRef, useState } from 'react';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { useFloatingCardPos } from '../../hooks/menu/useFloatingCardPos';
import type { SocketSlots } from '../../types/modifier.types';
import { Icon } from './Icon';
import type { ItemCardData } from './ItemCard';
import { CardDetail } from './CardDetail';
import './LoadoutBoard.scss';

export type SlotRef =
  | { kind: 'major' }
  | { kind: 'spell'; index: number }
  | { kind: 'minor'; index: number }
  | { kind: 'sketch'; index: 0 | 1 };

export type BoardRef =
  | SlotRef
  | { kind: 'socket'; minorId: string; socketIndex: number };

export interface SlotState {
  hasItem: boolean;
  icon?: string;
}

export interface LoadoutBoardSlots {
  major: SlotState;
  spells: SlotState[];
  minors: SlotState[];
  sketches: SlotState[];
}

export interface SocketSlotData {
  minorId: string;
  slotCount: number;
  /** modifierId per socket; empty socket = hole (`undefined`, or `null` after reload). */
  socketedIds: SocketSlots;
}

// Nav grid rows (cols are sparse; useMenuNavigation finds nearest neighbours).
const MAJOR_ROW = 0;
const SPELL_ROW = 1;
const MINOR_ROW = 2;
const SOCKET_ROW = 3;
const SKETCH_ROW = 4;

export function boardRefNavId(ref: BoardRef): string {
  switch (ref.kind) {
    case 'major':
      return 'slot:major';
    case 'socket':
      return `socket:${ref.minorId}:${ref.socketIndex}`;
    default:
      return `slot:${ref.kind}:${ref.index}`;
  }
}

function parseBoardRef(id: string): BoardRef | null {
  if (id === 'slot:major') {
    return { kind: 'major' };
  }
  if (id.startsWith('socket:')) {
    const [, minorId, idxStr] = id.split(':');
    return { kind: 'socket', minorId, socketIndex: parseInt(idxStr, 10) };
  }
  const m = /^slot:(spell|minor|sketch):(\d+)$/.exec(id);
  if (!m) {
    return null;
  }
  const idx = parseInt(m[2], 10);
  if (m[1] === 'sketch') {
    return { kind: 'sketch', index: idx === 0 ? 0 : 1 };
  }
  return { kind: m[1] as 'spell' | 'minor', index: idx };
}

function navCoords(ref: BoardRef): { row: number; col: number } {
  switch (ref.kind) {
    case 'major':
      return { row: MAJOR_ROW, col: 0 };
    case 'spell':
      return { row: SPELL_ROW, col: ref.index };
    case 'minor':
      return { row: MINOR_ROW, col: ref.index };
    case 'sketch':
      return { row: SKETCH_ROW, col: ref.index };
    case 'socket':
      return { row: SOCKET_ROW, col: 0 };
  }
}

interface LoadoutBoardProps {
  active: boolean;
  slots: LoadoutBoardSlots;
  /** Socket data per minor slot index (`null` = minor with no sockets). */
  sockets: (SocketSlotData | null)[];
  /** Which cells receive navigation (e.g. equipment page: sockets only).
   *  Non-navigable cells still render (read-only). Default: everything. */
  isNavigable?: (ref: BoardRef) => boolean;
  /** Detail card content for the focused cell. */
  getCard?: (ref: BoardRef) => ItemCardData | null;
  /** When set + active, focus jumps to this cell (e.g. returning from a picker). */
  focusTarget?: BoardRef | null;
  /** Return `false` to signal a denied action (drives the error cue). */
  onActivate: (ref: BoardRef) => void | boolean;
  onSecondary?: (ref: BoardRef) => void | boolean;
  onFocusChange?: (ref: BoardRef | null) => void;
  onCancel?: () => void;
}

const Cell = ({
  className,
  slot,
  navId,
  navRow,
  navCol,
  navigable,
}: {
  className: string;
  slot: SlotState;
  navId: string;
  navRow: number;
  navCol: number;
  navigable: boolean;
}) => (
  <div
    className={className}
    {...(navigable
      ? { 'data-nav-row': navRow, 'data-nav-col': navCol, 'data-nav-id': navId }
      : {})}
  >
    {slot.hasItem && (
      <Icon sprite={slot.icon} fill className="loadout-board-icon" />
    )}
  </div>
);

export const LoadoutBoard = ({
  active,
  slots,
  sockets,
  isNavigable,
  getCard,
  focusTarget,
  onActivate,
  onSecondary,
  onFocusChange,
  onCancel,
}: LoadoutBoardProps) => {
  const [detailsVisible, setDetailsVisible] = useState(true);
  const navOk = (ref: BoardRef) => (isNavigable ? isNavigable(ref) : true);

  const { containerRef, focusedId, setFocus } = useMenuNavigation({
    disabled: !active,
    onConfirm: (id) => {
      const ref = parseBoardRef(id);
      if (ref) {
        return onActivate(ref);
      }
    },
    onSecondary: (id) => {
      const ref = id ? parseBoardRef(id) : null;
      if (ref && onSecondary) {
        return onSecondary(ref);
      }
    },
    onDetails: () => setDetailsVisible((v) => !v),
    onCancel,
  });

  const focusedRef = active && focusedId ? parseBoardRef(focusedId) : null;

  // Initial focus lands on (0,0) = the major slot. In a restricted-navigation
  // mode (e.g. spell-only equip screen) that cell is non-navigable, leaving no
  // selection and no detail card — so on first activation, if (0,0) is not a
  // nav cell, jump to the first navigable cell in the DOM.
  const didInitFocus = useRef(false);
  useEffect(() => {
    if (!active || didInitFocus.current) {
      return;
    }
    didInitFocus.current = true;
    const c = containerRef.current;
    if (!c) {
      return;
    }
    if (c.querySelector('[data-nav-row="0"][data-nav-col="0"]')) {
      return;
    }
    const first = c.querySelector<HTMLElement>('[data-nav-row]');
    if (first) {
      setFocus(
        parseInt(first.dataset.navRow ?? '0', 10),
        parseInt(first.dataset.navCol ?? '0', 10),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Jump focus to a requested cell (e.g. back to the edited slot after a pick).
  useEffect(() => {
    if (active && focusTarget) {
      const { row, col } = navCoords(focusTarget);
      setFocus(row, col);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, active]);

  useEffect(() => {
    onFocusChange?.(focusedRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId, active]);

  const card = focusedRef && getCard ? getCard(focusedRef) : null;
  const cardPos = useFloatingCardPos(
    containerRef,
    detailsVisible ? focusedId : null,
  );

  return (
    <div className="loadout-board" ref={containerRef}>
      <div className="loadout-board-section loadout-board-major">
        <Cell
          className="loadout-board-cell"
          slot={slots.major}
          navId="slot:major"
          navRow={MAJOR_ROW}
          navCol={0}
          navigable={navOk({ kind: 'major' })}
        />
      </div>

      <div className="loadout-board-section loadout-board-trinkets">
        {[0, 1, 2, 3].map((col) => (
          <div className="loadout-board-trinket-col" key={col}>
            <Cell
              className="loadout-board-cell"
              slot={slots.spells[col]}
              navId={`slot:spell:${col}`}
              navRow={SPELL_ROW}
              navCol={col}
              navigable={navOk({ kind: 'spell', index: col })}
            />
            <Cell
              className="loadout-board-cell"
              slot={slots.minors[col]}
              navId={`slot:minor:${col}`}
              navRow={MINOR_ROW}
              navCol={col}
              navigable={navOk({ kind: 'minor', index: col })}
            />
            <div className="loadout-board-sockets">
              {sockets[col] &&
                Array.from({ length: sockets[col]!.slotCount }, (_, pip) => {
                  const minorId = sockets[col]!.minorId;
                  const filled = sockets[col]!.socketedIds[pip] != null;
                  const ref: BoardRef = {
                    kind: 'socket',
                    minorId,
                    socketIndex: pip,
                  };
                  return (
                    <div
                      key={pip}
                      className={`loadout-board-pip${filled ? ' loadout-board-pip--filled' : ''}`}
                      {...(navOk(ref)
                        ? {
                            'data-nav-row': SOCKET_ROW,
                            'data-nav-col': col * 3 + pip,
                            'data-nav-id': boardRefNavId(ref),
                          }
                        : {})}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      <div className="loadout-board-section loadout-board-sketches">
        {[0, 1].map((col) => (
          <Cell
            key={col}
            className="loadout-board-cell"
            slot={slots.sketches[col]}
            navId={`slot:sketch:${col}`}
            navRow={SKETCH_ROW}
            navCol={col}
            navigable={navOk({ kind: 'sketch', index: col as 0 | 1 })}
          />
        ))}
      </div>

      {detailsVisible && card && cardPos && (
        <CardDetail card={card} pos={cardPos} />
      )}
    </div>
  );
};
